import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Badge, Layout, Menu, Drawer, Button } from 'antd'
import { useSelector } from 'react-redux'
import { setCounterData, fetchMenu } from '../../store/user_menu/actions'
import IconComponent from '../../components/icon-component'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { IMenuItem } from './interfaces'
import { useEventSource } from '../../hooks/useEventSource/useEventSource'
import { pageParents } from './menuParents'
import { useAppDispatch } from '../../hooks/useAppDispatch/useAppDispatch'
import { SidebarIcon } from '../../assets/icons/sidebar'
import { Copyright, MenuHeader, MenuWrapper, OrganizationLogoWrapper } from './styles'
import { MenuOutlined, CloseOutlined } from '@ant-design/icons'
import { UserProfile } from './user-profile'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { createSelector } from 'reselect'
import { isHomeUrl, selectIsTenantOwner } from '../../pages_v2/home/owner-access'
import { hasPermission } from '../../lib/permissions'

const { Sider } = Layout
const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

const FloatingMenuButton = styled(Button)`
  position: fixed;
  bottom: 16px;
  right: 16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1005;
  border: none;

  svg {
    font-size: 24px;
  }
`

const MenuComponentInner: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { menu, counters } = useSelector((state: any) => state.menuV2)
  const controlPlaneExternal = Boolean(
    useSelector((state: any) => state?.session?.sessionData?.controlPlaneExternal),
  )
  const isTenantOwner = useSelector(selectIsTenantOwner)
  const canManageStaff = hasPermission(
    useSelector((state: any) => state?.session?.sessionData?.permissionsWithScope),
    'admin_organizations',
  )
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const storedState = localStorage.getItem('menuCollapsed')
    if (!storedState) return false
    try {
      return JSON.parse(storedState)
    } catch (_e) {
      return false
    }
  })
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const stored = localStorage.getItem('menuOpenKeys')
    if (!stored) return []
    try {
      const parsed = JSON.parse(stored)
      return Array.isArray(parsed) ? parsed : []
    } catch (_e) {
      return []
    }
  })
  const { sse, subscribe } = useEventSource()
  const isMobile = useMediaQuery({ maxWidth: 768 })

  useEffect(() => {
    dispatch(fetchMenu())
  }, [dispatch])

  useEffect(() => {
    console.log('MenuComponent mounted')
    return () => {
      console.log('MenuComponent unmounted')
    }
  }, [])

  useEffect(() => {
    if (menu && menu.length > 0) {
      const keys = findSelectedKeys(menu, location.pathname)
      setSelectedKeys(keys)

      if (!isCollapsed && !isMobile) {
        const parentKeys = findParentKeys(menu, location.pathname)
        setOpenKeys(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(parentKeys)) {
            localStorage.setItem('menuOpenKeys', JSON.stringify(parentKeys))
            return parentKeys
          }
          return prev
        })
      }
    }
  }, [location.pathname, menu, isCollapsed, isMobile])

  useEffect(() => {
    if (menu && menu.length > 0) {
      subscribe('/api/menu-counters', data => {
        dispatch(setCounterData(data))
      })
      return () => {
        sse.current?.close()
      }
    }
  }, [menu, subscribe, dispatch])

  const findSelectedKeys = (menuItems: IMenuItem[], pathname: string): string[] => {
    if (!menuItems || menuItems.length === 0) return []

    const parentPath = Object.keys(pageParents).find(parent => {
      const childPaths = pageParents[parent]
      if (Array.isArray(childPaths)) {
        return childPaths.some(childPath => pathname.startsWith(childPath))
      }
      return pathname.startsWith(childPaths)
    })

    const findPath = (
      items: IMenuItem[],
      path: string,
      parentIds: string[] = [],
    ): string[] | null => {
      for (const item of items) {
        const currentPath = item.url || ''
        if (currentPath && path === currentPath) {
          return [...parentIds, String(item.id)]
        }
        if (item.children?.length) {
          const result = findPath(item.children, path, [...parentIds, String(item.id)])
          if (result) return result
        }
      }
      return null
    }

    let result = findPath(menuItems, pathname)
    if (result) return result

    if (parentPath) {
      result = findPath(menuItems, parentPath)
      return result || []
    }

    return []
  }

  const findParentKeys = (menuItems: IMenuItem[], pathname: string): string[] => {
    if (!menuItems || menuItems.length === 0) return []

    const parentPath = Object.keys(pageParents).find(parent => {
      const childPaths = pageParents[parent]
      if (Array.isArray(childPaths)) {
        return childPaths.some(childPath => pathname.startsWith(childPath))
      }
      return pathname.startsWith(childPaths)
    })

    const findExactPath = (
      items: IMenuItem[],
      path: string,
      parentIds: string[] = [],
    ): string[] | null => {
      for (const item of items) {
        const currentPath = item.url || ''
        if (currentPath && path === currentPath) {
          return parentIds
        }
        if (item.children?.length) {
          const result = findExactPath(item.children, path, [...parentIds, String(item.id)])
          if (result) return result
        }
      }
      return null
    }

    let result = findExactPath(menuItems, pathname)
    if (result) return result

    if (parentPath) {
      result = findExactPath(menuItems, parentPath)
      return result || []
    }

    return []
  }

  const getUrlById = (arr: IMenuItem[], id: string): string | undefined => {
    for (const item of arr) {
      if (String(item.id) === id) return item.url
      if (item.children?.length) {
        const nestedResult = getUrlById(item.children, id)
        if (nestedResult) return nestedResult
      }
    }
  }

  const handleClick = useCallback(
    (e: any) => {
      if (e.domEvent?.ctrlKey || e.domEvent?.metaKey || e.domEvent?.button === 1) {
        return
      }
      setSelectedKeys([e.key])
      const targetUri = getUrlById(menu, e.key)
      if (targetUri) {
        navigate(targetUri)
        if (isMobile) setIsDrawerOpen(false)
      }
    },
    [menu, navigate, isMobile],
  )

  const handleCollapse = useCallback(() => {
    const newCollapsedState = !isCollapsed
    setIsCollapsed(newCollapsedState)
    localStorage.setItem('menuCollapsed', JSON.stringify(newCollapsedState))
    if (newCollapsedState) {
      setOpenKeys([])
      localStorage.removeItem('menuOpenKeys')
    } else {
      const parentKeys = findParentKeys(menu, location.pathname)
      setOpenKeys(parentKeys)
      localStorage.setItem('menuOpenKeys', JSON.stringify(parentKeys))
    }
  }, [isCollapsed, menu, location.pathname])

  const currentYear = new Date().getFullYear()

  const handleSubMenuClick = (openKeys: string[]) => {
    setOpenKeys(openKeys)
    localStorage.setItem('menuOpenKeys', JSON.stringify(openKeys))
  }

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen)
  }

  // Блокировка скролла фона при открытом мобильном меню
  useEffect(() => {
    if (isMobile && isDrawerOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.height = '100%'
    } else {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.height = ''
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.height = ''
    }
  }, [isMobile, isDrawerOpen])

  const menuItems = useMemo(() => {
    const calculateTotalNewItems = (items: IMenuItem[]): number => {
      return items.reduce((total, item) => {
        const count = counters[item.url] || 0
        return total + count + (item.children ? calculateTotalNewItems(item.children) : 0)
      }, 0)
    }

    const isDealersControlPlaneUrl = (url?: string) =>
      Boolean(url && String(url).startsWith('/admin/dealers'))

    const filterControlPlane = (items: IMenuItem[] = []): IMenuItem[] => {
      // «Главная» (/home) — только владельцу кабинета
      if (!controlPlaneExternal && isTenantOwner) return items
      return items
        .map(item => {
          const children = item.children ? filterControlPlane(item.children) : undefined
          if (controlPlaneExternal && isDealersControlPlaneUrl(item.url)) return null
          if (!isTenantOwner && isHomeUrl(item.url)) return null
          if (children && children.length === 0 && !item.url) return null
          return { ...item, children }
        })
        .filter(Boolean) as IMenuItem[]
    }

    const mapItems = (items: IMenuItem[] = []): any[] => {
      return items.map(item => {
        const newItemCount = counters[item.url] || 0
        const childrenTotal = calculateTotalNewItems(item.children || [])
        const totalCounter = newItemCount + childrenTotal
        const badgeCount = totalCounter > 0 ? totalCounter : newItemCount

        const hasChildren = item.children && item.children.length > 0

        const tourClassName =
          item.url === '/appeals'
            ? 'tour-menu-appeals'
            : item.url === '/orders'
            ? 'tour-menu-orders'
            : undefined

        const labelContent =
          item.url && !hasChildren ? (
            <Link to={item.url} style={{ color: 'inherit', textDecoration: 'none' }}>
              <span className="menu-item-label">
                {item.title}
                <Badge count={badgeCount} offset={[10, 0]} />
              </span>
            </Link>
          ) : (
            <span className="menu-item-label">
              {item.title}
              <Badge count={badgeCount} offset={[10, 0]} />
            </span>
          )

        if (hasChildren) {
          return {
            key: String(item.id),
            icon: item.icon ? <IconComponent iconName={item.icon} /> : null,
            label: labelContent,
            children: mapItems(item.children),
            className: tourClassName,
          }
        }

        return {
          key: String(item.id),
          icon: item.icon ? <IconComponent iconName={item.icon} /> : null,
          label: labelContent,
          className: tourClassName,
        }
      })
    }

    // ПРОТОТИП /personnel: пункт в меню без записи в БД — добавляем на фронте,
    // если в меню из БД его нет, а у пользователя есть admin_organizations.
    const withStaff = (items: IMenuItem[] = []): IMenuItem[] => {
      const hasUrl = (list: IMenuItem[]): boolean =>
        list.some(i => i.url === '/personnel' || (i.children ? hasUrl(i.children) : false))
      if (!canManageStaff || hasUrl(items)) return items
      return [
        ...items,
        { id: -1001, title: 'Сотрудники', url: '/personnel', icon: 'TeamOutlined' } as IMenuItem,
      ]
    }

    return mapItems(withStaff(filterControlPlane(menu)))
  }, [menu, counters, controlPlaneExternal, isTenantOwner, canManageStaff])

  // Проставляем data-tour-id на <li> элемента меню (antd Menu рендерит item как <li class="...">).
  useEffect(() => {
    const appealsLi = document.querySelector('li.tour-menu-appeals') as HTMLElement | null
    if (appealsLi) {
      appealsLi.setAttribute('data-tour-id', 'menu-appeals')
    }
    const ordersLi = document.querySelector('li.tour-menu-orders') as HTMLElement | null
    if (ordersLi) {
      ordersLi.setAttribute('data-tour-id', 'menu-orders')
    }
  }, [menuItems])

  const { sessionData } = useSelector(createSession)

  const organizationTitle = useMemo(() => {
    return sessionData?.organization?.title || null
  }, [sessionData])

  const organizationBrand = useMemo(() => {
    return sessionData?.organization?.brandLogo || null
  }, [sessionData])

  const menuContent = (
    <MenuWrapper>
      {organizationBrand && (
        <OrganizationLogoWrapper>
          <img src={organizationBrand} alt={organizationTitle} />
        </OrganizationLogoWrapper>
      )}
      <MenuHeader isCollapsed={isCollapsed}>
        {isMobile ? <UserProfile /> : <SidebarIcon onClick={handleCollapse} />}
      </MenuHeader>
      <Menu
        mode="inline"
        theme="dark"
        selectedKeys={selectedKeys}
        onClick={handleClick}
        style={{ flex: '1 1 auto' }}
        openKeys={openKeys}
        onOpenChange={handleSubMenuClick}
        items={menuItems}
      />
      {!isCollapsed && !isMobile && (
        <Copyright>© 2020-{currentYear} ООО «Аксиома». Все права защищены.</Copyright>
      )}
    </MenuWrapper>
  )

  return (
    <>
      {isMobile ? (
        <>
          <FloatingMenuButton onClick={toggleDrawer}>
            {isDrawerOpen ? <CloseOutlined /> : <MenuOutlined />}
          </FloatingMenuButton>
          <Drawer
            placement="left"
            onClose={toggleDrawer}
            open={isDrawerOpen}
            width={'100%'}
            styles={{
              body: { padding: 0 },
              header: { display: 'none' },
            }}
          >
            {menuContent}
          </Drawer>
        </>
      ) : (
        <Sider collapsed={isCollapsed} width={270} trigger={null}>
          {menuContent}
        </Sider>
      )}
    </>
  )
}

export const MenuComponent = React.memo(MenuComponentInner)
